import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setUserPermissions } from '../redux/slices/access/permissionSlice';
import { jwtDecode } from 'jwt-decode';
import axiosInstance from '../apiservices/axiosservice/axiosinstance';
import { API_ENDPOINTS } from '../apiservices/axiosservice/urls';

interface JwtPayload {
  exp: number;
}

export const useRehydrateAuth = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    const storedPermissions = localStorage.getItem('permissions');

    if (!accessToken) {
      // No token, mark rehydration complete
      dispatch(
        setUserPermissions({
          privileges: [],
          isAuthenticated: false,
          isRehydrating: false,
        })
      );
      return;
    }

    const refreshWithTimeout = (timeoutMs = 8000) => {
      return Promise.race([
        axiosInstance.post(API_ENDPOINTS.TOKEN_REFRESH),
        new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error('Session timeout. Login again')),
            timeoutMs
          )
        ),
      ]);
    };

    try {
      const decoded = jwtDecode<JwtPayload>(accessToken);
      const currentTime = Math.floor(Date.now() / 1000);

      if (decoded.exp > currentTime) {
        // Token is valid
        if (storedPermissions) {
          // Use stored permissions, no API call needed
          dispatch(
            setUserPermissions({
              privileges: JSON.parse(storedPermissions),
              isAuthenticated: true,
              isRehydrating: false,
            })
          );
        } else {
          // Permissions missing, fetch from server
          refreshWithTimeout()
            .then((response: any) => {
              const permissions = response.data.permissions;
              localStorage.setItem('permissions', JSON.stringify(permissions));
              dispatch(
                setUserPermissions({
                  privileges: permissions,
                  isAuthenticated: true,
                  isRehydrating: false,
                })
              );
            })
            .catch(() => {
              // Refresh failed, clear token
              localStorage.removeItem('accessToken');
              localStorage.removeItem('permissions');
              dispatch(
                setUserPermissions({
                  privileges: [],
                  isAuthenticated: false,
                  isRehydrating: false,
                })
              );
            });
        }
      } else {
        // Token expired, try to refresh
        refreshWithTimeout()
          .then((response: any) => {
            const permissions = response.data.permissions;
            const newAccessToken = response.data.accessToken;

            if (newAccessToken) {
              localStorage.setItem('accessToken', newAccessToken);
            }
            localStorage.setItem('permissions', JSON.stringify(permissions));

            dispatch(
              setUserPermissions({
                privileges: permissions,
                isAuthenticated: true,
                isRehydrating: false,
              })
            );
          })
          .catch(() => {
            // Refresh failed, clear everything
            localStorage.removeItem('accessToken');
            localStorage.removeItem('permissions');
            dispatch(
              setUserPermissions({
                privileges: [],
                isAuthenticated: false,
                isRehydrating: false,
              })
            );
          });
      }
    } catch (error) {
      // Invalid token, clear it
      localStorage.removeItem('accessToken');
      localStorage.removeItem('permissions');
      dispatch(
        setUserPermissions({
          privileges: [],
          isAuthenticated: false,
          isRehydrating: false,
        })
      );
    }
  }, [dispatch]);
};
